/**
 * CSV export, written for the spreadsheet it will actually be opened in.
 *
 * Three things a naive `rows.join(',')` gets wrong here:
 *
 * * **Formula injection.** Some cells are not ours: an AE title or a station
 *   name is whatever a hospital typed into a modality, and a cell beginning
 *   with `=`, `+`, `-`, `@`, tab or CR is executed as a formula by Excel and
 *   LibreOffice (`=HYPERLINK(...)` is the classic). Such a *text* cell is
 *   prefixed with `'`, which the spreadsheet shows as plain text. Numbers are
 *   left alone — a real negative number must stay a number.
 * * **Excel and UTF-8.** Without a byte-order mark Excel reads the file as the
 *   system code page and Arabic center names come out as mojibake. The BOM
 *   costs three bytes and every other reader ignores it.
 * * **Line endings.** RFC 4180 says CRLF, and Excel on Windows agrees.
 */

export type CsvCell = string | number | boolean | null | undefined;

const BOM = '﻿';
const FORMULA_START = /^[=+\-@\t\r]/;
const NEEDS_QUOTES = /[",\r\n]|^\s|\s$/;

/** One cell, escaped. `null`/`undefined`/non-finite numbers become empty —
 *  "not known" must not be exported as a 0 someone later sums. */
function cell(value: CsvCell): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  let text = value;
  if (FORMULA_START.test(text)) text = `'${text}`;
  return NEEDS_QUOTES.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** A complete CSV document: BOM, header row, then the rows, CRLF-terminated. */
export function toCsv(header: readonly string[], rows: readonly (readonly CsvCell[])[]): string {
  const lines = [header, ...rows].map((row) => row.map(cell).join(','));
  return `${BOM}${lines.join('\r\n')}\r\n`;
}

/**
 * Hand the viewer a file, entirely in the browser.
 *
 * The object URL is revoked on the next tick rather than immediately: some
 * browsers start the download asynchronously and a URL revoked in the same
 * tick yields an empty or failed file.
 */
export function downloadText(
  filename: string,
  text: string,
  type = 'text/plain;charset=utf-8',
): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** `toCsv` + `downloadText`, with the CSV media type. */
export function downloadCsv(
  filename: string,
  header: readonly string[],
  rows: readonly (readonly CsvCell[])[],
): void {
  downloadText(filename, toCsv(header, rows), 'text/csv;charset=utf-8');
}
